import type { FieldOrigin } from 'features/libra/ontology/ontologyFieldTypes'
import { useLibraAssetModelData } from 'features/libra/stepComponents/useLibraAssetModelData'
import { applyOriginOption } from 'features/libra/ticket/fieldOrigin'
import { buildFieldValuesPatch } from 'features/libra/ticket/fieldValuesPatch'
import type {
  LibraTicket,
  UpdateTicketArgs,
} from 'features/libra/ticket/libraTicketsApi'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import { useTicketWriter } from 'features/libra/ticket/TicketWriterContext'
import { useGeckoNotifications } from 'features/notifications'
import isEqual from 'lodash/isEqual'
import {
  createContext,
  type Dispatch,
  type FC,
  type PropsWithChildren,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  reconcileZoneSnapshot,
  useCofData,
  useConfidenceFromScreening,
  useZoneBreakdownJustificationData,
  useZoneData,
  ZONE_FIELD_IDS,
  ZoneAnalysisConfig,
  ZoneChangeGuard,
  ZoneParams,
  zoneParamsSchema,
} from './zoneAnalysisApi'
import { validateZoneGeometryChanges } from './zoneGeometryValidation'
import {
  patchZoneById,
  removeZoneById,
  replaceImageZoneProposals,
} from './zoneMutations'
import {
  acknowledgePageZoneOrigins,
  acknowledgeScopedZoneOrigins,
  latestZoneListOrigin,
  reconcileZoneOriginsAfterRefresh,
  trackZoneMutation,
  type ZoneOriginTracking,
} from './zoneOriginTracking'
import {
  hasDuplicateStoredZoneIds,
  hasEmptyStoredZoneId,
  matchesPersistedZoneSnapshot,
  type PersistedZoneSnapshot,
  prepareZonesForSave,
  reconcileZoneDrafts,
  replaceSavedZone,
} from './zoneSave'

interface ZoneDraftState extends ZoneOriginTracking {
  savedZones: readonly ZoneParams[]
  zones: ZoneParams[]
  savedJustification: string | undefined
  justification: string | undefined
  justificationOrigin: FieldOrigin | undefined
  identityConflict: PersistedZoneSnapshot | null
}

interface PreparedZoneSnapshot {
  snapshot: PersistedZoneSnapshot
  fieldExists: boolean
}

interface ZoneSaveError {
  zoneId?: string
  message: string
}

function hasDraftChanges(state: ZoneDraftState): boolean {
  return (
    !isEqual(
      state.zones.filter((zone) => !zone.ai_suggested),
      state.savedZones
    ) || state.justification !== state.savedJustification
  )
}

function trackZoneStateMutation(
  state: ZoneDraftState,
  zones: ZoneParams[]
): ZoneDraftState {
  return {
    ...state,
    zones,
    ...trackZoneMutation(
      {
        zoneOrigins: state.zoneOrigins,
        zoneMutationOrder: state.zoneMutationOrder,
      },
      state.zones,
      zones
    ),
  }
}

type ZoneAnalysisDataContextValue = ReturnType<typeof useZoneDataManagement> & {
  // Zone analysis only consumes the unit/model/inspection surface — pick
  // those fields explicitly so the context isn't broken when
  // `useLibraAssetModelData` is widened for unrelated callers (e.g. the
  // asset-workflow tab layer added `inspectionEntries`, `forceRefresh`,
  // etc.).
  assetModelData: Pick<
    ReturnType<typeof useLibraAssetModelData>,
    | 'unit'
    | 'assetModel'
    | 'siteName'
    | 'components'
    | 'inspections'
    | 'componentDataFrames'
  >
  config: ZoneAnalysisConfig
  confidence: 'low' | 'med' | 'high' // From NII Screening
}

const ZoneAnalysisDataContext = createContext<ZoneAnalysisDataContextValue>({
  assetModelData: {
    unit: undefined,
    assetModel: undefined,
    siteName: '',
    components: [],
    inspections: [],
    componentDataFrames: [],
  },
  zones: [],
  setZones: () => {},
  cof: { level: 'medium', justification: undefined },
  zoneBreakdownJustification: undefined,
  setZoneBreakdownJustification: () => {},
  applyAiZoneBreakdownJustification: () => {},
  config: { zoneFieldName: ZONE_FIELD_IDS.ZONES },
  confidence: 'med',
  hasChanges: false,
  hasPendingChanges: () => false,
  isZoneDirty: () => false,
  saveZone: async () => {},
  doSave: async () => {},
  resetChanges: () => {},
  isLoading: false,
  isSaving: false,
  savingZoneId: undefined,
  saveError: null,
  addZone: () => {},
  changeZone: () => {},
  removeZone: () => {},
  confirmProposal: () => {},
  replaceImageProposals: () => {},
  drawingResetKey: 0,
})

const identityConflictMessage =
  'A legacy zone identity is ambiguous, so no save was made. Reset discards unsaved changes and loads the latest ticket data; if IDs are still missing or duplicated, have the stored zone IDs corrected before saving.'

function hasLocalDraftChanges(state: ZoneDraftState): boolean {
  return (
    !isEqual(state.zones, state.savedZones) ||
    state.justification !== state.savedJustification
  )
}

function zoneIsDirty(state: ZoneDraftState, zoneId: string): boolean {
  const draft = state.zones.find(
    (zone) => zone.id === zoneId && !zone.ai_suggested
  )
  return Boolean(
    draft &&
    !isEqual(
      draft,
      state.savedZones.find((zone) => zone.id === zoneId)
    )
  )
}

function useZoneDataManagement(fieldName: string) {
  const {
    fieldValues,
    readOnly,
    ticketId,
    isLoading: isLoadingTicketData,
  } = useTicketAccess()
  const { enqueue } = useTicketWriter()
  const { errorNotification, successNotification } = useGeckoNotifications()
  const rawTicketZones = fieldValues[fieldName]?.value
  const { isLoading: isLoadingTicketZones, zones: ticketZones } =
    useZoneData(fieldName)
  const { isLoading: isLoadingCofData, cof } = useCofData()
  const {
    isLoading: isLoadingJustification,
    zoneBreakdownJustification: ticketJustification,
  } = useZoneBreakdownJustificationData()

  const [draftState, setDraftState] = useState<ZoneDraftState>(() => ({
    savedZones: ticketZones,
    zones: ticketZones,
    zoneOrigins: new Map(),
    zoneMutationOrder: 0,
    savedJustification: ticketJustification,
    justification: ticketJustification,
    justificationOrigin: undefined,
    identityConflict: null,
  }))
  const draftStateRef = useRef(draftState)
  const observedTicketRef = useRef<PersistedZoneSnapshot>({
    zones: ticketZones,
    justification: ticketJustification,
    rawZones: rawTicketZones,
  })
  const ownAcknowledgementRef = useRef<{
    snapshot: PersistedZoneSnapshot
    staleSnapshots: readonly PersistedZoneSnapshot[]
  } | null>(null)
  const ticketIdRef = useRef(ticketId)
  ticketIdRef.current = ticketId
  const savingRef = useRef(false)
  const mountedRef = useRef(false)
  const [isSaving, setIsSaving] = useState(false)
  const [savingZoneId, setSavingZoneId] = useState<string>()
  const [saveError, setSaveError] = useState<ZoneSaveError | null>(null)
  const [drawingResetKey, setDrawingResetKey] = useState(0)

  const commitState = useCallback(
    (update: (state: ZoneDraftState) => ZoneDraftState) => {
      const next = update(draftStateRef.current)
      draftStateRef.current = next
      setDraftState(next)
    },
    []
  )

  const updateZones = useCallback(
    (
      update: (zones: ZoneParams[]) => ZoneParams[],
      forceValidateIds: readonly string[] = []
    ) => {
      const current = draftStateRef.current.zones
      const next = update(current)
      if (isEqual(next, current)) return

      const geometryError = validateZoneGeometryChanges({
        baselineZones: current,
        proposedZones: next,
        forceValidateIds,
      })
      if (geometryError) {
        errorNotification(geometryError)
        return
      }

      commitState((state) => trackZoneStateMutation(state, next))
    },
    [commitState, errorNotification]
  )

  const addZone = useCallback(
    (zone: ZoneParams) =>
      updateZones((current) =>
        current.some((item) => item.id === zone.id)
          ? current
          : [...current, zone]
      ),
    [updateZones]
  )

  const changeZone = useCallback(
    (zoneId: string, updates: Partial<ZoneParams>, guard?: ZoneChangeGuard) =>
      updateZones((current) => patchZoneById(current, zoneId, updates, guard)),
    [updateZones]
  )

  const removeZone = useCallback(
    (zoneId: string) =>
      updateZones((current) => removeZoneById(current, zoneId)),
    [updateZones]
  )

  const confirmProposal = useCallback(
    (proposal: ZoneParams) =>
      updateZones(
        (current) => {
          const zone = current.find((item) => item.id === proposal.id)
          return zone?.ai_suggested
            ? current.map((item) =>
                item.id === proposal.id
                  ? { ...item, ai_suggested: false }
                  : item
              )
            : current
        },
        [proposal.id]
      ),
    [updateZones]
  )

  const replaceImageProposals = useCallback(
    (imageId: string, proposals: ZoneParams[]) =>
      commitState((state) =>
        trackZoneStateMutation(
          state,
          replaceImageZoneProposals(state.zones, imageId, proposals)
        )
      ),
    [commitState]
  )

  const acknowledgeTicketZones = useCallback(
    (snapshot: PersistedZoneSnapshot) => {
      const previous = observedTicketRef.current
      const pending = ownAcknowledgementRef.current
      ownAcknowledgementRef.current = {
        snapshot,
        staleSnapshots: [...(pending?.staleSnapshots ?? []), previous],
      }
      observedTicketRef.current = snapshot
    },
    []
  )

  const prepareLatestSnapshot = useCallback(
    (
      latest: LibraTicket,
      identityBaseline: PersistedZoneSnapshot
    ): PreparedZoneSnapshot => {
      const fieldExists = Object.prototype.hasOwnProperty.call(
        latest.fieldValues,
        fieldName
      )
      const rawZones = latest.fieldValues[fieldName]?.value
      const parsed = fieldExists
        ? zoneParamsSchema.array().safeParse(rawZones)
        : { success: true as const, data: [] }
      const reconciled = reconcileZoneSnapshot(
        identityBaseline.rawZones,
        identityBaseline.zones,
        rawZones,
        parsed.success ? parsed.data : []
      )
      const rawJustification =
        latest.fieldValues[ZONE_FIELD_IDS.BREAKDOWN_JUSTIFICATION]?.value
      const snapshot: PersistedZoneSnapshot = {
        zones: reconciled.zones,
        rawZones,
        justification:
          typeof rawJustification === 'string' ? rawJustification : undefined,
      }

      if (reconciled.hasAmbiguousLegacyChange) {
        commitState((current) => ({
          ...current,
          identityConflict: current.identityConflict ?? snapshot,
        }))
        throw new Error(identityConflictMessage)
      }
      if (!parsed.success) {
        throw new Error(
          'Saved zone data is invalid. Reload the ticket before saving.'
        )
      }

      return { snapshot, fieldExists }
    },
    [commitState, fieldName]
  )

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    const observed = observedTicketRef.current
    const ownAcknowledgement = ownAcknowledgementRef.current
    const incomingSnapshot = reconcileZoneSnapshot(
      observed.rawZones,
      observed.zones,
      rawTicketZones,
      ticketZones
    )
    const incoming: PersistedZoneSnapshot = {
      zones: incomingSnapshot.zones,
      justification: ticketJustification,
      rawZones: rawTicketZones,
    }
    const matchesOwnAcknowledgement = Boolean(
      ownAcknowledgement &&
      matchesPersistedZoneSnapshot(ownAcknowledgement.snapshot, incoming)
    )
    const matchesStaleAcknowledgement =
      !matchesOwnAcknowledgement &&
      ownAcknowledgement?.staleSnapshots.some((snapshot) =>
        matchesPersistedZoneSnapshot(snapshot, {
          zones: ticketZones,
          rawZones: rawTicketZones,
          justification: ticketJustification,
        })
      )
    if (
      ownAcknowledgement &&
      !matchesOwnAcknowledgement &&
      matchesStaleAcknowledgement
    ) {
      return
    }
    if (ownAcknowledgement) ownAcknowledgementRef.current = null
    if (matchesPersistedZoneSnapshot(observed, incoming)) return
    if (savingRef.current) return

    observedTicketRef.current = incoming
    const hasAmbiguousIdentity =
      !matchesOwnAcknowledgement && incomingSnapshot.hasAmbiguousLegacyChange
    const current = draftStateRef.current
    if (current.identityConflict) {
      commitState((state) => ({
        ...state,
        identityConflict: incoming,
      }))
      return
    }
    if (hasAmbiguousIdentity && hasLocalDraftChanges(current)) {
      commitState((state) => ({
        ...state,
        identityConflict: incoming,
      }))
      setSaveError({ message: identityConflictMessage })
      errorNotification(identityConflictMessage)
      return
    }
    commitState((state) => {
      const zones = reconcileZoneDrafts(
        state.savedZones,
        state.zones,
        incoming.zones
      )
      const justification =
        state.justification === state.savedJustification
          ? ticketJustification
          : state.justification
      return {
        ...state,
        savedZones: incoming.zones,
        zones,
        zoneOrigins: reconcileZoneOriginsAfterRefresh(
          state.zoneOrigins,
          zones,
          state.savedZones,
          incoming.zones
        ),
        savedJustification: ticketJustification,
        justification,
        justificationOrigin:
          justification === ticketJustification
            ? undefined
            : state.justificationOrigin,
      }
    })
  }, [
    ticketZones,
    ticketJustification,
    rawTicketZones,
    isSaving,
    commitState,
    errorNotification,
  ])

  const setZones = useCallback<Dispatch<SetStateAction<ZoneParams[]>>>(
    (update) => {
      commitState((current) =>
        trackZoneStateMutation(
          current,
          typeof update === 'function' ? update(current.zones) : update
        )
      )
    },
    [commitState]
  )

  const setZoneBreakdownJustification = useCallback<
    Dispatch<SetStateAction<string | undefined>>
  >(
    (update) => {
      commitState((current) => {
        const justification =
          typeof update === 'function' ? update(current.justification) : update
        return {
          ...current,
          justification,
          justificationOrigin:
            justification === current.savedJustification ? undefined : 'human',
        }
      })
    },
    [commitState]
  )

  const applyAiZoneBreakdownJustification = useCallback(
    (justification: string) => {
      commitState((current) => {
        return {
          ...current,
          justification,
          justificationOrigin:
            justification === current.savedJustification ? undefined : 'llm',
        }
      })
    },
    [commitState]
  )

  const hasChanges = useMemo(() => hasDraftChanges(draftState), [draftState])
  const hasPendingChanges = useCallback(
    () => hasDraftChanges(draftStateRef.current),
    []
  )
  const isZoneDirty = useCallback(
    (zoneId: string) => zoneIsDirty(draftState, zoneId),
    [draftState]
  )

  const runSave = useCallback(
    async (
      zoneId: string | undefined,
      identityBaseline: PersistedZoneSnapshot,
      buildWrite: (
        latest: LibraTicket,
        prepared: PreparedZoneSnapshot
      ) => Omit<UpdateTicketArgs, 'ticketId'>,
      onSaved: (savedZones: readonly ZoneParams[], rawZones: unknown) => void,
      successMessage: string,
      allowMissingZoneField = false
    ) => {
      if (savingRef.current) {
        const error = new Error(
          'Another save is in progress. Wait for it to finish before saving again.'
        )
        errorNotification(error.message)
        throw error
      }
      if (draftStateRef.current.identityConflict) {
        const error = new Error(identityConflictMessage)
        setSaveError({ zoneId, message: error.message })
        errorNotification(error.message)
        throw error
      }
      if (readOnly) {
        const error = new Error('This ticket is read-only and cannot be saved.')
        errorNotification(error.message)
        throw error
      }

      const savingTicketId = ticketId
      savingRef.current = true
      setIsSaving(true)
      setSavingZoneId(zoneId)
      setSaveError(null)

      try {
        const applied = await enqueue(
          (latest) => {
            if (latest.id !== savingTicketId) {
              throw new Error('The ticket changed before this save could run.')
            }
            const prepared = prepareLatestSnapshot(latest, identityBaseline)
            return buildWrite(latest, prepared)
          },
          { applyInteractionSideEffects: true }
        )
        if (applied === null) {
          throw new Error('The save was not applied. Retry the save.')
        }
        if (!mountedRef.current || ticketIdRef.current !== savingTicketId) {
          throw new Error('The ticket changed before this save completed.')
        }

        const savedZoneField = applied.fieldValues?.[fieldName]
        if (!savedZoneField || savedZoneField.value === undefined) {
          if (allowMissingZoneField) {
            onSaved(identityBaseline.zones, identityBaseline.rawZones)
            setSaveError(null)
            successNotification(successMessage)
            return
          }
          throw new Error(
            'The applied save result did not contain valid zone data. Retry the save.'
          )
        }
        const parsedSavedZones = zoneParamsSchema
          .array()
          .safeParse(savedZoneField.value)
        if (!parsedSavedZones.success) {
          throw new Error(
            'The applied save result did not contain valid zone data. Retry the save.'
          )
        }

        const acknowledgedSnapshot = reconcileZoneSnapshot(
          identityBaseline.rawZones,
          identityBaseline.zones,
          savedZoneField.value,
          parsedSavedZones.data
        )
        onSaved(acknowledgedSnapshot.zones, savedZoneField.value)
        setSaveError(null)
        successNotification(successMessage)
      } catch (error) {
        if (mountedRef.current && ticketIdRef.current === savingTicketId) {
          const message =
            error instanceof Error
              ? error.message
              : 'The save failed. Retry the save.'
          setSaveError({ zoneId, message })
          errorNotification(message)
        }
        throw error
      } finally {
        savingRef.current = false
        if (mountedRef.current && ticketIdRef.current === savingTicketId) {
          setIsSaving(false)
          setSavingZoneId(undefined)
        }
      }
    },
    [
      enqueue,
      errorNotification,
      fieldName,
      readOnly,
      prepareLatestSnapshot,
      successNotification,
      ticketId,
    ]
  )

  const doSave = useCallback(async () => {
    const snapshot = draftStateRef.current
    const submittedZones: readonly ZoneParams[] = snapshot.zones.filter(
      (zone) => !zone.ai_suggested
    )
    const submittedZoneListOrigin = latestZoneListOrigin(snapshot.zoneOrigins)
    const submittedMutationOrder = snapshot.zoneMutationOrder
    const submittedJustification = snapshot.justification
    const submittedJustificationOrigin = snapshot.justificationOrigin
    const persistedJustification = submittedJustification ?? ''
    const identityBaseline = observedTicketRef.current
    const zoneFieldChanged = !isEqual(submittedZones, identityBaseline.zones)
    const shouldWriteZoneField =
      zoneFieldChanged ||
      identityBaseline.zones.length > 0 ||
      identityBaseline.rawZones !== undefined

    await runSave(
      undefined,
      identityBaseline,
      (latest, prepared) => {
        const latestRawZones = prepared.snapshot.rawZones
        const latestZones = prepared.snapshot.zones
        if (hasEmptyStoredZoneId(latestRawZones)) {
          throw new Error(
            'Saved zones contain an empty stored ID. Correct the stored zone data before using page Save.'
          )
        }
        if (hasDuplicateStoredZoneIds(latestRawZones)) {
          throw new Error(
            'Saved zones contain duplicate stored IDs. Have the stored IDs corrected before using page Save.'
          )
        }
        const geometryError = validateZoneGeometryChanges({
          baselineZones: latestZones,
          proposedZones: submittedZones,
        })
        if (geometryError) throw new Error(geometryError)
        const savedZones = shouldWriteZoneField
          ? prepareZonesForSave({
              rawZones: latestRawZones,
              previousZones: snapshot.savedZones,
              latestZones,
              proposedZones: submittedZones,
            })
          : []

        const values = applyOriginOption(
          {
            ...(shouldWriteZoneField
              ? { [fieldName]: { value: savedZones } }
              : {}),
            [ZONE_FIELD_IDS.BREAKDOWN_JUSTIFICATION]: {
              value: persistedJustification,
            },
          },
          {
            origin: {
              ...(shouldWriteZoneField
                ? { [fieldName]: submittedZoneListOrigin }
                : {}),
              [ZONE_FIELD_IDS.BREAKDOWN_JUSTIFICATION]:
                submittedJustificationOrigin,
            },
          }
        )
        return {
          fieldValues: buildFieldValuesPatch(
            latest.fieldValues,
            { kind: 'ticket' },
            values
          ),
        }
      },
      (savedZones, savedRawZones) => {
        acknowledgeTicketZones({
          zones: savedZones,
          rawZones: savedRawZones,
          justification: persistedJustification,
        })
        commitState((current) => {
          const zones = reconcileZoneDrafts(
            submittedZones,
            current.zones,
            savedZones
          )
          const justification =
            current.justification === submittedJustification
              ? persistedJustification
              : current.justification
          return {
            ...current,
            savedZones,
            zones,
            zoneOrigins: acknowledgePageZoneOrigins(
              current.zoneOrigins,
              zones,
              savedZones,
              submittedMutationOrder
            ),
            savedJustification: persistedJustification,
            justification,
            justificationOrigin:
              justification === persistedJustification
                ? undefined
                : current.justificationOrigin,
          }
        })
      },
      'NII zone changes saved.',
      !shouldWriteZoneField
    )
  }, [acknowledgeTicketZones, commitState, fieldName, runSave])

  const saveZone = useCallback(
    async (zoneId: string) => {
      const snapshot = draftStateRef.current
      const selectedZone = snapshot.zones.find((zone) => zone.id === zoneId)
      if (!selectedZone) {
        const error = new Error(
          'This zone is no longer available. Refresh and retry.'
        )
        setSaveError({ zoneId, message: error.message })
        errorNotification(error.message)
        throw error
      }
      const wasPreviouslySaved = snapshot.savedZones.some(
        (zone) => zone.id === zoneId
      )
      const selectedOrigin =
        snapshot.zoneOrigins.get(zoneId)?.scopedFieldOrigin ?? 'human'
      const identityBaseline = observedTicketRef.current

      await runSave(
        zoneId,
        identityBaseline,
        (latest, prepared) => {
          if (selectedZone.ai_suggested) {
            throw new Error('Confirm this AI-suggested zone before saving it.')
          }
          if (!zoneIsDirty(snapshot, zoneId)) {
            throw new Error('This zone has no unsaved changes.')
          }

          const { fieldExists, snapshot: latestSnapshot } = prepared
          const rawZones = latestSnapshot.rawZones
          const latestZones = latestSnapshot.zones
          const nextRawZones = replaceSavedZone(
            fieldExists,
            rawZones,
            selectedZone,
            wasPreviouslySaved,
            snapshot.savedZones
          )
          const geometryError = validateZoneGeometryChanges({
            baselineZones: latestZones,
            proposedZones: zoneParamsSchema.array().parse(nextRawZones),
          })
          if (geometryError) throw new Error(geometryError)

          const values = applyOriginOption(
            { [fieldName]: { value: nextRawZones } },
            {
              origin: {
                [fieldName]: selectedOrigin,
              },
            }
          )
          return {
            fieldValues: buildFieldValuesPatch(
              latest.fieldValues,
              { kind: 'ticket' },
              values
            ),
          }
        },
        (savedZones, savedRawZones) => {
          acknowledgeTicketZones({
            zones: savedZones,
            rawZones: savedRawZones,
            justification: observedTicketRef.current.justification,
          })
          const comparisonBaseline = snapshot.savedZones.some(
            (zone) => zone.id === zoneId
          )
            ? snapshot.savedZones.map((zone) =>
                zone.id === zoneId ? selectedZone : zone
              )
            : [...snapshot.savedZones, selectedZone]
          commitState((current) => {
            const zones = reconcileZoneDrafts(
              comparisonBaseline,
              current.zones,
              savedZones
            )
            return {
              ...current,
              savedZones,
              zones,
              zoneOrigins: acknowledgeScopedZoneOrigins(
                current.zoneOrigins,
                zones,
                savedZones,
                identityBaseline.zones,
                zoneId
              ),
            }
          })
        },
        `Zone ${selectedZone?.title ?? ''} saved.`
      )
    },
    [acknowledgeTicketZones, commitState, errorNotification, fieldName, runSave]
  )

  const resetChanges = useCallback(() => {
    commitState((current) => {
      const savedZones = current.identityConflict?.zones ?? current.savedZones
      const savedJustification = current.identityConflict
        ? current.identityConflict.justification
        : current.savedJustification
      if (current.identityConflict) {
        acknowledgeTicketZones(current.identityConflict)
      }
      return {
        ...current,
        savedZones,
        zones: [...savedZones],
        zoneOrigins: new Map(),
        savedJustification,
        justification: savedJustification,
        justificationOrigin: undefined,
        identityConflict: null,
      }
    })
    setSaveError(null)
    setDrawingResetKey((current) => current + 1)
  }, [acknowledgeTicketZones, commitState])

  return useMemo(
    () => ({
      zones: draftState.zones,
      setZones,
      addZone,
      changeZone,
      removeZone,
      confirmProposal,
      replaceImageProposals,
      cof,
      zoneBreakdownJustification: draftState.justification,
      setZoneBreakdownJustification,
      applyAiZoneBreakdownJustification,
      doSave,
      saveZone,
      hasChanges,
      hasPendingChanges,
      isZoneDirty,
      resetChanges,
      drawingResetKey,
      isLoading:
        isLoadingTicketData ||
        isLoadingTicketZones ||
        isLoadingCofData ||
        isLoadingJustification ||
        isSaving,
      isSaving,
      savingZoneId,
      saveError,
    }),
    [
      draftState,
      setZones,
      addZone,
      changeZone,
      removeZone,
      confirmProposal,
      replaceImageProposals,
      cof,
      setZoneBreakdownJustification,
      applyAiZoneBreakdownJustification,
      doSave,
      saveZone,
      hasChanges,
      hasPendingChanges,
      isZoneDirty,
      resetChanges,
      drawingResetKey,
      isLoadingTicketData,
      isLoadingTicketZones,
      isLoadingCofData,
      isLoadingJustification,
      isSaving,
      savingZoneId,
      saveError,
    ]
  )
}

export const ZoneAnalysisDataProvider: FC<
  PropsWithChildren<{ config: ZoneAnalysisConfig }>
> = ({ children, config }) => {
  const assetModelDataRaw = useLibraAssetModelData()
  const assetModelData = useMemo(
    () => ({
      unit: assetModelDataRaw.unit,
      assetModel: assetModelDataRaw.assetModel,
      siteName: assetModelDataRaw.siteName,
      components: assetModelDataRaw.components,
      inspections: assetModelDataRaw.inspections,
      componentDataFrames: assetModelDataRaw.componentDataFrames,
    }),
    [
      assetModelDataRaw.unit,
      assetModelDataRaw.assetModel,
      assetModelDataRaw.siteName,
      assetModelDataRaw.components,
      assetModelDataRaw.inspections,
      assetModelDataRaw.componentDataFrames,
    ]
  )

  const zoneData = useZoneDataManagement(config.zoneFieldName)

  // Read confidence from NII Screening step
  const confidence = useConfidenceFromScreening(config.confidenceFieldName)

  const value = useMemo(
    () => ({
      assetModelData,
      ...zoneData,
      config,
      confidence,
      isLoading: zoneData.isLoading || assetModelDataRaw.isLoading,
    }),
    [assetModelData, assetModelDataRaw.isLoading, config, confidence, zoneData]
  )

  return (
    <ZoneAnalysisDataContext.Provider value={value}>
      {children}
    </ZoneAnalysisDataContext.Provider>
  )
}

export const useZoneAnalysisData = () => {
  const context = useContext(ZoneAnalysisDataContext)
  if (!context) {
    throw new Error(
      'useZoneAnalysisData must be used within ZoneAnalysisDataProvider'
    )
  }
  return context
}
